import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, CheckCheck, Clock, AlertCircle, FileText, Paperclip, Send, Loader2, ExternalLink, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Tick = ({ s }) => {
  if (s === "read") return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
  if (s === "delivered") return <CheckCheck className="w-3.5 h-3.5" />;
  if (s === "sent") return <Check className="w-3.5 h-3.5" />;
  if (s === "failed") return <AlertCircle className="w-3.5 h-3.5 text-rose-500" />;
  return <Clock className="w-3.5 h-3.5" />;
};

const openMedia = async (m) => {
  try {
    const r = await api.get(`/whatsapp/media/${m.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  } catch { toast.error("File non ancora disponibile"); }
};

const Bubble = ({ m }) => {
  const out = m.direction === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${out ? "bg-emerald-600 text-white rounded-br-md" : "bg-card border border-border rounded-bl-md"}`}>
        {m.media && (
          <button type="button" onClick={() => openMedia(m)} className={`flex items-center gap-2 mb-1 underline-offset-2 hover:underline ${out ? "text-white" : "text-primary"}`} data-testid={`wa-media-${m.id}`}>
            <FileText className="w-4 h-4" /> {m.media.filename || (m.type === "image" ? "Immagine" : m.type === "audio" ? "Messaggio vocale" : "Allegato")}
          </button>
        )}
        {m.body && <div className="whitespace-pre-wrap break-words" data-no-translate>{m.body}</div>}
        <div className={`flex items-center gap-1 justify-end text-[10px] mt-1 ${out ? "text-white/80" : "text-muted-foreground"}`}>
          {m.source === "telefono" && <Smartphone className="w-3 h-3" />}{fmtDateTime(m.created_at)} {out && <Tick s={m.status} />}
        </div>
        {m.status === "failed" && m.error && <div className="text-[11px] mt-1 text-rose-100">{m.error}</div>}
      </div>
    </div>
  );
};

export const ChatThread = ({ waId, onBack, onSent }) => {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const load = () => api.get(`/whatsapp/conversations/${waId}`).then((r) => setT(r.data)).catch(() => {});
  useEffect(() => { setT(null); load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [waId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [t?.messages?.length]);

  const run = async (fn) => {
    setSending(true);
    try { await fn(); await load(); onSent?.(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const send = () => text.trim() && run(async () => { await api.post("/whatsapp/send", { to: waId, text }); setText(""); });
  const sendFile = (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("to", waId); fd.append("caption", text); fd.append("file", file);
    run(async () => { await api.post("/whatsapp/send-file", fd); setText(""); });
  };

  const title = t?.contact || t?.name || `+${waId}`;
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="wa-thread">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={onBack} data-testid="wa-back-button"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1 min-w-0"><div className="font-heading font-bold truncate" data-no-translate>{title}</div><div className="text-xs text-muted-foreground" data-no-translate>+{waId}</div></div>
        <a href={`https://wa.me/${waId}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid="wa-open-phone"><ExternalLink className="w-4 h-4 mr-1.5" /> Apri WhatsApp</Button></a>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 bg-secondary/30">
        {!t && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
        {t?.messages.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>
      {t && !t.window_open ? (
        <div className="border-t border-border p-4 text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200" data-testid="wa-window-closed">
          Sono passate più di 24 ore dall'ultimo messaggio del cliente: WhatsApp non permette di scrivergli da qui. Usa «Apri WhatsApp» per scrivergli dal telefono.
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex items-end gap-2">
          <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={sendFile} data-testid="wa-file-input" />
          <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={() => fileRef.current?.click()} disabled={sending} data-testid="wa-attach-button"><Paperclip className="w-4 h-4" /></Button>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Scrivi la risposta…" className="min-h-[44px] max-h-40 resize-none"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="wa-input" />
          <Button type="submit" size="icon" className="h-11 w-11 shrink-0 bg-emerald-600 hover:bg-emerald-700" disabled={sending || !text.trim()} data-testid="wa-send-button">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      )}
    </div>
  );
};

export default ChatThread;
